import { useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";

// project imports
import Loader from "components/Loader";
import useAuth from "hooks/useAuth";

// types
import { GuardProps } from "types/auth";

// ==============================|| AUTH GUARD ||============================== //

const WEB_APP_ALLOWED_POSITIONS = new Set([
  "admin",
  "manager",
  "lead_supervisor",
]);
const ADMIN_ONLY_PATH_PREFIXES = [
  "/pages/",
  "/pearson-map",
  "/facility-map",
  "/facility-modelling",
  "/airport-map",
  "/washroom-map",
  "/task-settings",
  "/task-settings-2",
  "/task-history",
  "/shift-scheduling",
  "/shift-scheduler",
  "/staffing-calendar",
  "/master-schedule",
  "/task-lines",
  "/task-assignment",
  "/task-scheduling",
  "/staff-management",
  "/edit-timeline",
  "/deterioration",
  "/administration/user-management/admin-setting",
];

export default function AuthGuard({ children }: GuardProps) {
  const { isInitialized, isLoggedIn, user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    if (isInitialized && !isLoggedIn) {
      navigate("/login", {
        state: { from: location.pathname },
        replace: true,
      });
      return;
    }

    if (!isInitialized || !isLoggedIn) return;

    const positionKey = String(user?.positionKey || "").toLowerCase();
    const hasWebAccess = WEB_APP_ALLOWED_POSITIONS.has(positionKey);
    if (!hasWebAccess) {
      if (typeof logout === "function") logout();
      navigate("/login", {
        state: {
          from: location.pathname,
          authError:
            "Web app access is restricted to admin, lead_supervisor, and manager roles.",
        },
        replace: true,
      });
      return;
    }

    const isAdmin = positionKey === "admin";
    if (!isAdmin) {
      const currentPath = location.pathname || "/";
      const adminOnly = ADMIN_ONLY_PATH_PREFIXES.some(
        (prefix) => currentPath === prefix || currentPath.startsWith(prefix),
      );
      if (adminOnly) {
        navigate("/dashboard", { replace: true });
      }
    }
  }, [
    isInitialized,
    isLoggedIn,
    navigate,
    location.pathname,
    logout,
    user?.positionKey,
  ]);

  if (!isInitialized) {
    return <Loader />;
  }

  return children;
}
